import React, { useReducer, useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { hasAnyRole } from "../../Utils/Common";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import GridOnIcon from "@mui/icons-material/GridOn";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";

import { templateReducer, starterTemplate, acceptsField, tableFieldConflict } from "./templateSchema";
import { SOURCES } from "./fieldCatalog";
import SectionPalette from "./components/SectionPalette";
import FieldTreePanel from "./components/FieldTreePanel";
import BuilderCanvas from "./components/BuilderCanvas";
import PropertiesPanel from "./components/PropertiesPanel";
import GenerateReportDialog from "./generate/GenerateReportDialog";
import { saveReportTemplate, getReportTemplateById } from "../../services/ReportTemplate";
import { loadRecord, loadRecordOptions } from "../../services/ReportRecordLoader";

const DEFAULT_SOURCE = "SUB_ORDER";

export default function ReportBuilderPage() {
  const history = useHistory();
  const { templateId } = useParams();
  const theme = useTheme();
  const immersive = useMediaQuery(theme.breakpoints.down("lg")); // tablet + mobile

  const [template, dispatch] = useReducer(templateReducer, undefined, () => starterTemplate(DEFAULT_SOURCE));
  const [selectedId, setSelectedId] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);

  const [previewRecord, setPreviewRecord] = useState(null);
  const [previewRecords, setPreviewRecords] = useState([]);
  const [recordOptions, setRecordOptions] = useState([]);
  const [pickedOption, setPickedOption] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [loadingTemplate, setLoadingTemplate] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const toast = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  // Generate-only users must not reach the builder by URL — send them to the list.
  useEffect(() => {
    if (!hasAnyRole(["ROLE_ADMIN", "REPORT_CREATE"])) {
      history.replace("/template/report-builder");
    }
  }, [history]);

  // Load an existing template when editing.
  useEffect(() => {
    let cancelled = false;
    if (!templateId) return undefined;
    (async () => {
      const server = await getReportTemplateById(templateId);
      if (cancelled) return;
      if (!server) {
        toast("Could not load that template.", "error");
        setLoadingTemplate(false);
        return;
      }
      const doc = server.layout && server.layout.sections ? server.layout : starterTemplate(DEFAULT_SOURCE);
      dispatch({
        type: "LOAD_TEMPLATE",
        template: {
          ...doc,
          id: server.id,
          name: server.name || doc.name,
          domain: server.domain || doc.domain,
          visibility: server.visibility || doc.visibility || "PRIVATE",
        },
      });
      setLoadingTemplate(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Fetch a sample record + picker options whenever the source changes.
  useEffect(() => {
    let cancelled = false;
    if (loadingTemplate) return undefined;
    const source = template.source;
    setLoadingPreview(true);
    setPickedOption(null);
    (async () => {
      // 1) Slim header list for the dropdown + list-table rows (no full data).
      const options = await loadRecordOptions(source);
      if (cancelled) return;
      setRecordOptions(options);
      setPreviewRecords(options.map((o) => o.raw).filter(Boolean));
      // 2) Full detail for just the first record (drives the WYSIWYG preview).
      const first = options[0];
      const sample = first ? await loadRecord(source, first.id) : null;
      if (cancelled) return;
      setPreviewRecord(sample);
      setLoadingPreview(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.source, loadingTemplate]);

  // Auto-select a freshly added section.
  useEffect(() => {
    if (template._lastAdded) setSelectedId(template._lastAdded);
  }, [template._lastAdded]);

  // Set by the computed-column formula box so field drops can be routed to it.
  const formulaAppendRef = useRef(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Press-and-hold to drag on touch so normal taps/scrolls still work.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveDrag(null);
      if (!over) return;
      const a = active.data.current;
      const o = over.data.current;
      if (!a) return;

      if (a.kind === "PALETTE_SECTION") {
        let index = template.sections.length;
        if (o && o.kind === "SECTION") {
          const at = template.sections.findIndex((s) => s.id === o.sectionId);
          index = at < 0 ? template.sections.length : at + 1;
        }
        dispatch({ type: "ADD_SECTION", sectionType: a.sectionType, index });
        return;
      }
      if (a.kind === "FIELD") {
        if (o && o.kind === "FORMULA_DROP") {
          if (formulaAppendRef.current) formulaAppendRef.current(a.field);
          return;
        }
        if (o && o.kind === "SECTION") {
          const target = template.sections.find((s) => s.id === o.sectionId);
          if (target && acceptsField(target.type, a.field)) {
            const conflict = tableFieldConflict(target, a.field);
            if (conflict) {
              toast(conflict, "warning");
              return;
            }
            dispatch({ type: "ADD_FIELD_TO_SECTION", id: o.sectionId, field: a.field });
            setSelectedId(o.sectionId);
          }
        }
        return;
      }
      if (a.kind === "SECTION") {
        if (o && o.kind === "SECTION" && a.sectionId !== o.sectionId) {
          dispatch({ type: "MOVE_SECTION", fromId: a.sectionId, toId: o.sectionId });
        }
      }
    },
    [template.sections]
  );

  const handleSourceChange = (nextSource) => {
    if (nextSource === template.source) return;
    if (!window.confirm("Switching the data source starts a fresh template for that source. Continue?")) return;
    dispatch({ type: "LOAD_TEMPLATE", template: starterTemplate(nextSource) });
    setSelectedId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    const { _lastAdded, ...doc } = template;
    const payload = {
      id: doc.id || null,
      name: doc.name,
      domain: doc.domain,
      layout: doc,
      visibility: doc.visibility || "PRIVATE",
    };
    const saved = await saveReportTemplate(payload);
    setSaving(false);
    if (saved && saved.id) {
      dispatch({ type: "SET_META", patch: { id: saved.id } });
      toast("Template saved.");
    } else {
      toast("Save failed. Check your connection and try again.", "error");
    }
  };

  const handlePickRecord = async (option) => {
    setPickedOption(option);
    if (!option) return;
    setLoadingPreview(true);
    const full = await loadRecord(template.source, option.id);
    setPreviewRecord(full);
    setLoadingPreview(false);
  };

  const selectedSection = template.sections.find((s) => s.id === selectedId) || null;

  if (loadingTemplate) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const railSx = { overflowY: "auto", p: 1.5, height: "100%" };

  // On tablet/mobile the builder goes full-screen over the app header + side menu.
  const rootSx = immersive
    ? {
        position: "fixed",
        inset: 0,
        zIndex: (t) => t.zIndex.drawer + 2,
        bgcolor: "background.default",
        p: 1,
        display: "flex",
        flexDirection: "column",
      }
    : { height: "calc(100vh - 130px)", display: "flex", flexDirection: "column" };

  return (
    <Box sx={rootSx}>
      {/* Toolbar — minimal (Back + Save) on tablet/mobile */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/template/report-builder")}>
          Back
        </Button>

        {!immersive && (
          <>
            <TextField
              size="small"
              value={template.name}
              onChange={(e) => dispatch({ type: "SET_META", patch: { name: e.target.value } })}
              sx={{ width: 220 }}
            />
            <TextField
              select
              size="small"
              label="Data source"
              value={template.source}
              onChange={(e) => handleSourceChange(e.target.value)}
              sx={{ width: 190 }}
            >
              {SOURCES.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            <Autocomplete
              size="small"
              sx={{ width: 250 }}
              options={recordOptions}
              value={pickedOption}
              onChange={(_, opt) => handlePickRecord(opt)}
              getOptionLabel={(o) => (o && o.label) || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Preview record" />}
            />
            {loadingPreview && <CircularProgress size={18} />}
          </>
        )}

        <Box sx={{ flex: 1 }} />

        {!immersive && (
          <FormControlLabel
            control={
              <Switch
                checked={template.visibility === "SHARED"}
                onChange={(e) => dispatch({ type: "SET_META", patch: { visibility: e.target.checked ? "SHARED" : "PRIVATE" } })}
              />
            }
            label="Share with team"
          />
        )}
        <Button variant={immersive ? "contained" : "outlined"} startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
        {!immersive && (
          <Button variant="contained" color="success" startIcon={<GridOnIcon />} onClick={() => setGenOpen(true)}>
            Generate Excel
          </Button>
        )}
      </Paper>

      {/* 3-pane workspace */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveDrag(e.active.data.current)}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
        {immersive ? (
          /* Tablet / mobile: single scrolling column.
             Sections + Data fields + Sheet settings grouped; Live preview shown separately. */
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionPalette />
              <Divider sx={{ my: 1.5 }} />
              <FieldTreePanel source={template.source} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <PropertiesPanel
                template={template}
                section={selectedSection}
                dispatch={dispatch}
                formulaAppendRef={formulaAppendRef}
              />
            </Paper>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
                Live preview
              </Typography>
              <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
                <Paper elevation={2} sx={{ p: 1.5, minHeight: 300 }}>
                  <BuilderCanvas
                    template={template}
                    previewRecord={previewRecord}
                    previewRecords={previewRecords}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    dispatch={dispatch}
                    activeDrag={activeDrag}
                  />
                </Paper>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Desktop: 3-column layout */
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 1 }}>
            <Paper variant="outlined" sx={{ width: 270, flexShrink: 0, ...railSx }}>
              <SectionPalette />
              <Divider sx={{ my: 1.5 }} />
              <FieldTreePanel source={template.source} />
            </Paper>

            <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
              <Paper elevation={2} sx={{ maxWidth: 760, mx: "auto", p: 1.5, minHeight: 300 }}>
                <BuilderCanvas
                  template={template}
                  previewRecord={previewRecord}
                  previewRecords={previewRecords}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  dispatch={dispatch}
                  activeDrag={activeDrag}
                />
              </Paper>
            </Box>

            <Paper variant="outlined" sx={{ width: 300, flexShrink: 0, ...railSx }}>
              <PropertiesPanel template={template} section={selectedSection} dispatch={dispatch} formulaAppendRef={formulaAppendRef} />
            </Paper>
          </Box>
        )}

        <DragOverlay>
          {activeDrag && activeDrag.kind === "FIELD" ? (
            <Chip color="info" variant="filled" label={activeDrag.field.label} />
          ) : activeDrag && activeDrag.kind === "PALETTE_SECTION" ? (
            <Paper sx={{ px: 1.5, py: 1 }}>{activeDrag.sectionType}</Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <GenerateReportDialog open={genOpen} onClose={() => setGenOpen(false)} template={template} />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
